import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  STUDIO_MODE_NAV_ITEMS,
  type StudioModeKey,
  visibleStudioModeNavItems,
} from '@/constants/studio-nav'
import { useTenantFeatures } from '@/hooks'
import { cn } from '@/shared/lib/utils'

const MODE_TAB_CLASS =
  'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition'

const MODE_TAB_ACTIVE_CLASS =
  'border-indigo-400/45 bg-indigo-500/15 text-indigo-100 ring-1 ring-inset ring-indigo-400/35'

const MODE_TAB_IDLE_CLASS =
  'border-zinc-700/90 bg-zinc-900/70 text-zinc-400 hover:border-zinc-600 hover:bg-zinc-900 hover:text-zinc-200'

type StudioModeTabsProps = {
  active: StudioModeKey
  className?: string
}

/** 创作中心列表页：制片 / 编剧 / 自由创作 模式切换 */
export function StudioModeTabs({ active, className }: StudioModeTabsProps) {
  const { t } = useTranslation()
  const { features } = useTenantFeatures()
  const modes = visibleStudioModeNavItems(features)

  if (modes.length <= 1) return null

  return (
    <nav
      aria-label={t('modeHub.title')}
      className={cn(
        'mb-4 flex gap-2 overflow-x-auto py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {modes.map((mode) => {
        const Icon = mode.icon
        const isActive = mode.key === active
        return (
          <NavLink
            key={mode.key}
            to={mode.to}
            className={cn(MODE_TAB_CLASS, isActive ? MODE_TAB_ACTIVE_CLASS : MODE_TAB_IDLE_CLASS)}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
            {t(mode.titleKey)}
          </NavLink>
        )
      })}
    </nav>
  )
}

export function studioModeFromPath(pathname: string): StudioModeKey | null {
  for (const item of STUDIO_MODE_NAV_ITEMS) {
    if (pathname === item.to || pathname.startsWith(`${item.to}/`)) return item.key
  }
  return null
}
